import { ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { FormsModule } from '@angular/forms';
import { AppRoutingModule } from './app-routing.module';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { NgIf } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatCardModule } from '@angular/material/card';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, NativeDateModule } from '@angular/material/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTreeModule } from '@angular/material/tree';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MatRadioModule } from '@angular/material/radio';
import { MatExpansionModule } from '@angular/material/expansion';

import { AuthConfigModule } from './auth/auth-config.module';
import { AuthInterceptor } from 'angular-auth-oidc-client';

import { MibDatePipe } from './utils/pipes/mib.datepipe';
import { MibTimeDatePipe } from './utils/pipes/mibtime.datepipe';
import { MibYearPipe } from './utils/pipes/mibyear.datepipe';
import { ReportUrlPipe } from './utils/pipes/reportUrl.pipe';
import { CapitalizePipe } from './utils/pipes/capitalize.pipe';
import { KeysPipe } from './utils/pipes/keys.pipe';
import { IsNotReportUrlPipe } from './utils/pipes/isNotReportUrl';
import { DefaultPipe } from './utils/pipes/default.pipe';

import { AppComponent } from './app.component';
import { AssetDetailComponent } from './components/assets/asset-detail/asset-detail.component';
import { AssetServiceRecommendationsComponent } from './components/service-recommendations/asset-service-recommendations/asset-service-recommendations.component';
import { AssetServiceEventsComponent } from './components/service-events/asset-service-events/asset-service-events.component';
import { AssetSearchComponent } from './components/assets/asset-search/asset-search.component';
import { ImportAssetsComponent } from './components/assets/import-assets/import-assets.component';
import { CreateAssetComponent } from './components/assets/create-asset/create-asset.component';
import { EditAssetComponent } from './components/assets/edit-asset/edit-asset.component';
import { CustomerDetailsComponent } from './components/customers/customer-details/customer-details.component';
import { SiteDetailsComponent } from './components/sites/site-details/site-details.component';
import { LocationDetailsComponent } from './components/locations/location-details/location-details.component';
import { LoadingBarComponent } from './components/utils/loading-bar/loading-bar.component';
import { ImportRegistrationsComponent } from './components/registrations/import-registrations/import-registrations.component';
import { RegistrationDetailsComponent } from './components/registrations/registration-details/registration-details.component';
import { AssetSearchResultItemComponent } from './components/assets/asset-search-result-item/asset-search-result-item.component';
import { ServiceRecommendationDetailsComponent } from './components/service-recommendations/service-recommendation-details/service-recommendation-details.component';
import { LoginComponent } from './components/auth/login/login.component';
import { CustomerSearchComponent } from './components/customers/customer-search/customer-search.component';
import { SearchPageComponent } from './components/search-page/search-page.component';
import { CustomerSearchResultItemComponent } from './components/customers/customer-search-result-item/customer-search-result-item.component';
import { CustomerRegistrationDetailsComponent } from './components/customers/customer-registration-details/customer-registration-details.component';
import { TraceableComponentDetailsComponent } from './components/traceable-components/traceable-component-details/traceable-component-details.component';
import { CiamLoginComponent } from './components/auth/ciam-login/ciam-login.component';
import { UnathorizedPageComponent } from './components/auth/unathorized-page/unathorized-page.component';
import { LogoutPageComponent } from './components/auth/logout-page/logout-page.component';
import { AssetTechnicalDetailsComponent } from './components/assets/asset-technical-details/asset-technical-details.component';
import { TraceableComponentsTreeComponent } from './components/traceable-components/traceable-components-tree/traceable-components-tree.component';
import { NotFoundPageComponent } from './components/not-found-page/not-found-page.component';
import { AssetLinkComponent } from './components/assets/asset-link/asset-link.component';
import { TraceableComponentLinkComponent } from './components/traceable-components/traceable-component-link/traceable-component-link.component';
import { SparePartsListComponent } from './components/spare-parts/spare-parts-list/spare-parts-list.component';
import { SparePartDetailsComponent } from './components/spare-parts/spare-part-details/spare-part-details.component';
import { AssetSparePartsComponent } from './components/spare-parts/asset-spare-parts/asset-spare-parts.component';
import { ImportSparePartsComponent } from './components/spare-parts/import-spare-parts/import-spare-parts.component';

import { TabsGroupComponent } from './components/tabs/tabs-group/tabs-group.component';
import { TabContentComponent } from './components/tabs/tab-content/tab-content.component';

import { SiteTreeComponent } from './components/sites/site-tree/site-tree.component';
import { SiteSearchComponent } from './components/sites/site-search/site-search.component';
import { SiteSearchResultItemComponent } from './components/sites/site-search-result-item/site-search-result-item.component';
import { AssetPageComponent } from './components/assets/asset-page/asset-page.component';
import { AssetTechnicalDetailsEditComponent } from './components/assets/asset-technical-details-edit/asset-technical-details-edit.component';
import { LocationTreeComponent } from './components/locations/location-tree/location-tree.component';
import { SiteLocationTreeComponent } from './components/sites/site-location-tree/site-location-tree.component';
import { SiteProductTreeComponent } from './components/sites/site-product-tree/site-product-tree.component';


@NgModule({
    declarations: [
        AppComponent,
        AssetSearchComponent,
        AssetDetailComponent,
        AssetServiceRecommendationsComponent,
        AssetServiceEventsComponent,
        ImportAssetsComponent,
        ImportRegistrationsComponent,
        CreateAssetComponent,
        EditAssetComponent,
        MibDatePipe,
        MibTimeDatePipe,
        MibYearPipe,
        ReportUrlPipe,
        RegistrationDetailsComponent,
        CustomerDetailsComponent,
        SiteDetailsComponent,
        LocationDetailsComponent,
        LoadingBarComponent,
        AssetSearchResultItemComponent,
        LocationTreeComponent,
        ServiceRecommendationDetailsComponent,
        LoginComponent,
        CustomerSearchComponent,
        SearchPageComponent,
        CustomerSearchResultItemComponent,
        CustomerRegistrationDetailsComponent,
        TraceableComponentDetailsComponent,
        CapitalizePipe,
        KeysPipe,
        DefaultPipe,
        CiamLoginComponent,
        UnathorizedPageComponent,
        LogoutPageComponent,
        AssetTechnicalDetailsComponent,
        TraceableComponentsTreeComponent,
        NotFoundPageComponent,
        AssetLinkComponent,
        TraceableComponentLinkComponent,
        SparePartsListComponent,
        SparePartDetailsComponent,
        AssetSparePartsComponent,
        ImportSparePartsComponent,
        TabContentComponent,
        TabsGroupComponent,
        SiteTreeComponent,
        SiteSearchComponent,
        SiteSearchResultItemComponent,
        AssetPageComponent,
        SiteProductTreeComponent,
        SiteLocationTreeComponent,
        AssetTechnicalDetailsEditComponent,
        IsNotReportUrlPipe
    ],
    bootstrap: [AppComponent], imports: [BrowserModule,
        AppRoutingModule,
        FormsModule,
        BrowserAnimationsModule,
        NgIf,
        AuthConfigModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatIconModule,
        MatTableModule,
        MatTabsModule,
        MatMenuModule,
        MatDividerModule,
        MatProgressSpinnerModule,
        MatProgressBarModule,
        MatCardModule,
        MatDatepickerModule,
        NativeDateModule,
        MatTooltipModule,
        MatToolbarModule,
        MatSelectModule,
        MatCheckboxModule,
        MatTreeModule,
        MatCheckboxModule,
        MatRadioModule,
        MatExpansionModule], providers: [
            {
                //to enable momend date adapter, instead cannot set different datepicker format
                provide: DateAdapter,
                useClass: MomentDateAdapter,
                deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
            },
            {
                //to have utc date for saving correct date in datepickers
                provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS,
                useValue: {
                    useUtc: true
                }
            },
            {
                //to set datepickers format, needs all formats to have proper parsing, but not sure about accessibility
                provide: MAT_DATE_FORMATS,
                useValue: {
                    parse: {
                        dateInput: ['yyyy-MM-DD', 'DD-MM-yyyy'] // Set the format for parsing date from input, two formats to handle all use cases
                    },
                    display: {
                        dateInput: 'yyyy-MM-DD', // Set the format for displaying date in input
                        monthYearLabel: 'yyyy-MM-DD', // Set the format for month and year in datepicker header
                        dateA11yLabel: 'yyyy-MM-DD', // Set the format for date accessibility label
                        monthYearA11yLabel: 'yyyy-MM-DD', // Set the format for month and year accessibility label
                    },
                }
            },
            {
                //handles adding access tokens to requests, probably also refresh tokens
                provide: HTTP_INTERCEPTORS,
                useClass: AuthInterceptor,
                multi: true,
            },
            provideHttpClient(withInterceptorsFromDi()),
        ]
})
export class AppModule { }
